import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table, TableBody, TableContainer, TableHead } from '@/core/ui';
import { TableBodyEmpty, TableHeadRow } from '@/shared/components';
import { getTableBodyColumnCount } from '@/shared/helpers';
import { getSelectedItem } from '@/warehouse/helpers';
import { useCallback, useEffect, useRef } from 'react';
import { HideScrollerEdgeContainer, TableScrollerBox } from '@/core/styled';
import { engRusLetterOrNumberOnlyRegex } from '@/core/utils/validation';
import { getItemWorkflowTableRowState } from '@/shared/helpers';
import { catchArrowUpDownEventClassName } from '@/warehouse/constants';
import { closeSnackbar } from 'notistack';
import { useEventListener } from 'usehooks-ts';
import { useItemWorkflowTableStore } from './business';
import { ItemWorkflowTableRowMemoized } from './components';
import { scrollItemIntoView } from './helpers';
const ItemWorkflowTable = ({ data, isLoading, isFetching, isError, isWaitingForSocketConnection, error, headColumns, tableScrollingContainerRef, disableCatchWindowFocus, itemIdField, itemContentContext, itemContent, onRowDoubleClick, onEnterKeyDown, onDeleteKeyDown, onMinusKeyDown, onPlusKeyDown, onLetterOrNumberKeyDown, }) => {
    const currentItemIndex = useItemWorkflowTableStore((state) => state.currentItemIndex);
    const setCurrentItemIndex = useItemWorkflowTableStore((state) => state.setCurrentItemIndex);
    const listRef = useRef(null);
    const rowCount = data?.length ?? 0;
    useEffect(() => {
        return () => {
            // Prevents `currentItemIndex` persist it's value even when there is already another table is shown.
            setCurrentItemIndex(-1);
            closeSnackbar();
        };
    }, [setCurrentItemIndex]);
    useEffect(() => {
        if (disableCatchWindowFocus)
            return;
        if (isLoading || rowCount === 0)
            return;
        listRef.current?.focus();
    }, [isLoading, rowCount, disableCatchWindowFocus]);
    // TODO: this should be reworked, according to useCatchWindowFocus hook, so event listener should be removed
    // on disableCatchWindowFocus
    //
    const handleWindowArrowKeyDown = (event) => {
        if (disableCatchWindowFocus)
            return;
        // Catching focus inside table container on 'ArrowUp' and 'ArrowDown' events from body and other "watchable" elements.
        if (!((event.code === 'ArrowUp' || event.code === 'ArrowDown') &&
            (event.target?.tagName === 'BODY' ||
                // (event.target as HTMLElement)?.classList.contains(catchInsideWindowKeyDownEventClassName))
                event.target?.classList.contains(catchArrowUpDownEventClassName)))) {
            return;
        }
        event.preventDefault();
        // Important! Prevents event from fire twice.
        event.stopImmediatePropagation();
        listRef?.current?.focus();
        keyDownCallback(event);
    };
    useEventListener('keydown', handleWindowArrowKeyDown);
    const keyDownCallback = useCallback((event) => {
        // TODO: to delete
        // console.log('TABLE event.key', event.key);
        // console.log('TABLE event.code', event.code);
        // Or to use event.key === 'Enter'
        if ((event.code === 'Enter' || event.code === 'NumpadEnter') && onEnterKeyDown) {
            const currentItem = getSelectedItem(data, currentItemIndex);
            // Pass through even if currentItem is undefined to play error sound.
            onEnterKeyDown(currentItem);
            return;
        }
        if (event.code === 'Delete' && event.ctrlKey && onDeleteKeyDown) {
            // For Ctrl+Del
            const currentItem = getSelectedItem(data, currentItemIndex);
            if (currentItem)
                onDeleteKeyDown(currentItem);
            return;
        }
        if ((event.code === 'Equal' || event.code === 'NumpadAdd') && onPlusKeyDown) {
            // For +/=
            const currentItem = getSelectedItem(data, currentItemIndex);
            if (currentItem)
                onPlusKeyDown(currentItem);
            return;
        }
        if ((event.code === 'Minus' || event.code === 'NumpadSubtract') && onMinusKeyDown) {
            // For -/_
            const currentItem = getSelectedItem(data, currentItemIndex);
            if (currentItem)
                onMinusKeyDown(currentItem);
            return;
        }
        if (onLetterOrNumberKeyDown && (event.key === 'Backspace' || engRusLetterOrNumberOnlyRegex.test(event.key))) {
            onLetterOrNumberKeyDown(event);
            return;
        }
        let nextIndex;
        if (event.code === 'ArrowUp') {
            nextIndex = Math.max(0, currentItemIndex - 1);
            if (currentItemIndex > rowCount - 1)
                nextIndex = rowCount - 1;
        }
        else if (event.code === 'ArrowDown') {
            nextIndex = Math.min(rowCount - 1, currentItemIndex + 1);
        }
        else {
            return;
        }
        if (event.target !== listRef.current) {
            setCurrentItemIndex(nextIndex);
        }
        if ((event.code === 'ArrowUp' && currentItemIndex >= 0) ||
            (event.code === 'ArrowDown' && currentItemIndex < rowCount - 1)) {
            scrollItemIntoView(tableScrollingContainerRef, nextIndex, event.code);
        }
        setCurrentItemIndex(nextIndex);
        event.preventDefault();
    }, [
        currentItemIndex,
        rowCount,
        data,
        onDeleteKeyDown,
        onEnterKeyDown,
        onLetterOrNumberKeyDown,
        onMinusKeyDown,
        onPlusKeyDown,
        setCurrentItemIndex,
        tableScrollingContainerRef,
    ]);
    // TODO: mb rename to something better? `TableContainerRef`?
    const scrollerRef = useCallback((element) => {
        if (element) {
            element.addEventListener('keydown', keyDownCallback);
            listRef.current = element;
        }
        else {
            listRef.current?.removeEventListener('keydown', keyDownCallback);
        }
    }, [keyDownCallback]);
    // const fixedHeaderContent = useCallback(() => <TableHeadRow columns={columns} />, [columns]);
    const bodyColumnCount = getTableBodyColumnCount(headColumns);
    return (_jsx(HideScrollerEdgeContainer, { children: _jsx(TableContainer, { ref: scrollerRef, 
            // Makes it possible to be focused by `listRef.current.focus()` method.
            tabIndex: -1, onMouseDown: () => { }, onFocus: () => {
                if (currentItemIndex === -1)
                    setCurrentItemIndex(0);
            }, onBlur: () => { }, children: _jsx(TableScrollerBox, { ref: tableScrollingContainerRef, children: _jsxs(Table, { size: "medium", stickyHeader: true, sideCellSpacing: 4, children: [_jsx(TableHead, { children: _jsx(TableHeadRow, { headColumns: headColumns }) }), _jsxs(TableBody, { children: [_jsx(TableBodyEmpty, { isLoading: isLoading, isError: isError, error: error, columnCount: bodyColumnCount, isEmpty: !rowCount, isWaitingForSocketConnection: isWaitingForSocketConnection }), !isLoading &&
                                    data?.map((item, index) => {
                                        const context = {
                                            ...(itemContentContext || {}),
                                            currentItemIndex,
                                            columnCount: bodyColumnCount,
                                            isLoading,
                                            isFetching,
                                            isError,
                                            error,
                                            isEmpty: !rowCount,
                                            setCurrentItemIndex,
                                            onRowDoubleClick,
                                        };
                                        return (_jsx(ItemWorkflowTableRowMemoized, { hover: isWaitingForSocketConnection ? false : true, rowState: isWaitingForSocketConnection
                                                ? 'disabled'
                                                : getItemWorkflowTableRowState(item.quantity, 'receiptedQuantity' in item ? item.receiptedQuantity : item.pickedQuantity, 'blockedQuantity' in item ? item.blockedQuantity : undefined), selected: currentItemIndex === index, index: index, item: item, context: context, itemContent: itemContent, 
                                            // rowContent={rowContent}
                                            onClick: () => {
                                                if (isWaitingForSocketConnection)
                                                    return;
                                                setCurrentItemIndex(index);
                                            }, onDoubleClick: () => {
                                                if (isWaitingForSocketConnection)
                                                    return;
                                                onRowDoubleClick?.(item);
                                            } }, item[itemIdField]));
                                    })] })] }) }) }) }));
};
export { ItemWorkflowTable };
